import React, { useState, useRef, useEffect } from 'react';
import { Calendar as CalendarIcon, ChevronLeft, ChevronRight } from 'lucide-react';

const DateRangePicker = ({ onChange, initialRange }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [range, setRange] = useState(initialRange || {
        start: formatDate(new Date(new Date().setDate(new Date().getDate() - 30))),
        end: formatDate(new Date())
    });
    const [currentMonth, setCurrentMonth] = useState(new Date());
    const containerRef = useRef(null);
    const triggerRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (containerRef.current && !containerRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Escape закрывает поповер и возвращает фокус на кнопку периода
    useEffect(() => {
        if (!isOpen) return undefined;
        const onKey = (e) => {
            if (e.key === 'Escape') {
                setIsOpen(false);
                triggerRef.current?.focus();
            }
        };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [isOpen]);

    const presets = [
        { label: 'Сегодня', getValue: () => ({ start: formatDate(new Date()), end: formatDate(new Date()) }) },
        {
            label: 'Вчера', getValue: () => {
                const yesterday = new Date();
                yesterday.setDate(yesterday.getDate() - 1);
                return { start: formatDate(yesterday), end: formatDate(yesterday) };
            }
        },
        {
            label: 'Последние 7 дней', getValue: () => {
                const start = new Date();
                start.setDate(start.getDate() - 7);
                return { start: formatDate(start), end: formatDate(new Date()) };
            }
        },
        {
            label: 'Последние 30 дней', getValue: () => {
                const start = new Date();
                start.setDate(start.getDate() - 30);
                return { start: formatDate(start), end: formatDate(new Date()) };
            }
        },
        {
            label: 'Этот месяц', getValue: () => {
                const now = new Date();
                const start = new Date(now.getFullYear(), now.getMonth(), 1);
                return { start: formatDate(start), end: formatDate(now) };
            }
        },
        {
            label: 'Прошлый месяц', getValue: () => {
                const now = new Date();
                const start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
                const end = new Date(now.getFullYear(), now.getMonth(), 0);
                return { start: formatDate(start), end: formatDate(end) };
            }
        }
    ];

    function formatDate(date) {
        const d = new Date(date);
        const year = d.getFullYear();
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    }

    const handlePresetClick = (preset) => {
        const newRange = preset.getValue();
        setRange(newRange);
        onChange(newRange);
        setIsOpen(false);
    };

    const handleDateClick = (dateStr) => {
        let newRange;
        if (!range.start || (range.start && range.end)) {
            newRange = { start: dateStr, end: null };
        } else {
            if (dateStr < range.start) {
                newRange = { start: dateStr, end: range.start };
            } else {
                newRange = { start: range.start, end: dateStr };
            }
            onChange(newRange);
            setIsOpen(false);
        }
        setRange(newRange);
    };

    const renderCalendar = () => {
        const year = currentMonth.getFullYear();
        const month = currentMonth.getMonth();
        const firstDay = new Date(year, month, 1);
        const lastDay = new Date(year, month + 1, 0);
        const daysInMonth = lastDay.getDate();
        const startDay = (firstDay.getDay() + 6) % 7; // Adjust to start from Monday

        const days = [];
        for (let i = 0; i < startDay; i++) {
            days.push(<span key={`empty-${i}`} className="adm-drp-day is-empty" aria-hidden="true" />);
        }

        for (let d = 1; d <= daysInMonth; d++) {
            const date = new Date(year, month, d);
            const dateStr = formatDate(date);
            const isStart = range.start === dateStr;
            const isEnd = range.end === dateStr;
            const isInRange = range.start && range.end && dateStr > range.start && dateStr < range.end;

            days.push(
                <button
                    type="button"
                    key={d}
                    className={`adm-drp-day${isStart || isEnd ? ' is-selected' : ''}${isInRange ? ' is-in-range' : ''}`}
                    aria-pressed={isStart || isEnd}
                    aria-label={date.toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', year: 'numeric' })}
                    onClick={() => handleDateClick(dateStr)}
                >
                    {d}
                </button>
            );
        }

        return days;
    };

    const changeMonth = (offset) => {
        const newMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + offset, 1);
        setCurrentMonth(newMonth);
    };

    const monthName = currentMonth.toLocaleString('ru-RU', { month: 'long', year: 'numeric' });
    const rangeText = `${range.start ? new Date(range.start).toLocaleDateString('ru-RU') : '...'} — ${range.end ? new Date(range.end).toLocaleDateString('ru-RU') : '...'}`;

    return (
        <div className="adm-drp" ref={containerRef}>
            <button
                ref={triggerRef}
                type="button"
                className="adm-drp-trigger"
                aria-haspopup="dialog"
                aria-expanded={isOpen}
                aria-label={`Период: ${rangeText}`}
                onClick={() => setIsOpen(!isOpen)}
            >
                <CalendarIcon size={18} aria-hidden="true" />
                <span className="adm-drp-value">{rangeText}</span>
            </button>

            {isOpen && (
                <div className="adm-drp-pop" role="dialog" aria-label="Выбор периода">
                    <div className="adm-drp-presets">
                        {presets.map((p, i) => (
                            <button type="button" key={i} className="adm-drp-preset" onClick={() => handlePresetClick(p)}>
                                {p.label}
                            </button>
                        ))}
                    </div>
                    <div className="adm-drp-cal">
                        <div className="adm-drp-head">
                            <button type="button" className="adm-drp-nav" aria-label="Предыдущий месяц" onClick={() => changeMonth(-1)}>
                                <ChevronLeft size={16} aria-hidden="true" />
                            </button>
                            <span className="adm-drp-month" aria-live="polite">{monthName}</span>
                            <button type="button" className="adm-drp-nav" aria-label="Следующий месяц" onClick={() => changeMonth(1)}>
                                <ChevronRight size={16} aria-hidden="true" />
                            </button>
                        </div>
                        <div className="adm-drp-weekdays" aria-hidden="true">
                            {['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'].map(d => <span key={d}>{d}</span>)}
                        </div>
                        <div className="adm-drp-grid">
                            {renderCalendar()}
                        </div>
                        {range.start && !range.end && (
                            <p className="adm-drp-note">Выберите дату окончания</p>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default DateRangePicker;
